import React, { useState, useEffect } from 'react';
import {
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import { Download as DownloadIcon, Upload as UploadIcon } from '@mui/icons-material';
import MatchDataService from '../services/MatchDataService';
import { useAuth } from '../contexts/AuthContext';
import UserTierService from '../models/UserTier';
import firebaseService from '../services/FirebaseService';

const MatchDataPull = ({ onDataPulled }) => {
  const { currentUser, userProfile } = useAuth();
  const [matchId, setMatchId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  const [pullsToday, setPullsToday] = useState(0);

  // Load match pull count on mount
  useEffect(() => {
    const loadPullCount = async () => {
      if (currentUser) {
        const count = await firebaseService.getMatchPullsToday(currentUser.uid);
        setPullsToday(count);
      }
    };
    loadPullCount();
  }, [currentUser]);

  const showNotification = (message, severity = 'info') => {
    setNotification({
      open: true,
      message,
      severity,
    });
  };

  const pullMatchData = async () => {
    if (!matchId.trim()) {
      setError('Please enter a match ID');
      return;
    }

    // Check tier limits before pulling
    const limitCheck = UserTierService.canPullMatchData(userProfile, pullsToday);
    if (!limitCheck.canPull) {
      setError(limitCheck.reason);
      showNotification(limitCheck.reason, 'error');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Show initial status
      showNotification('Checking OpenDota API...', 'info');

      const matchData = await MatchDataService.pullMatchData(matchId.trim());

      // Extract laning matchup data FIRST (before parsing, using raw OpenDota data)
      console.log('🔍 Attempting to extract laning matchups from raw match data:', matchData);
      const laningMatchups = MatchDataService.extractLaningMatchups(matchData);
      console.log('🔍 Extracted laning matchups result:', laningMatchups);

      // Then parse the data for the draft board
      const parsedData = MatchDataService.parseMatchDataToPicksFormat(matchData);

      // Add laning matchups to parsed data if available
      if (laningMatchups) {
        parsedData.practiceMatchups = laningMatchups;
        console.log('✅ Laning matchups extracted and added to draft:', laningMatchups);
      } else {
        console.warn('⚠️ No laning matchups extracted from match data');
      }

      if (onDataPulled) {
        onDataPulled(parsedData);
      }

      // Increment match pull counter
      if (currentUser) {
        await firebaseService.incrementMatchPull(currentUser.uid);
        setPullsToday(prev => prev + 1);
      }

      // Show success message with source
      const source = matchData.source || 'OpenDota';
      showNotification(`✅ Match data loaded from ${source}!`, 'success');

      // Don't clear the match ID - keep it in the input box
      setIsLoading(false);

    } catch (err) {
      setIsLoading(false);
      console.error('Error pulling match data:', err);

      // Better error messages
      let errorMessage = err.message;

      if (errorMessage.includes('not supported in this browser')) {
        errorMessage = 'Local file access requires Chrome or Edge browser';
      } else if (errorMessage.includes('Folder selection cancelled')) {
        errorMessage = 'Folder selection was cancelled';
      } else if (errorMessage.includes('not found')) {
        errorMessage = `Match ${matchId} not found on OpenDota or in local files`;
      }

      setError(errorMessage);
      showNotification(errorMessage, 'error');
    }
  };

  const loadMatchDataFile = async () => {
    try {
      setIsLoading(true);
      setError('');

      const matchData = await MatchDataService.loadMatchDataFile();
      const parsedData = MatchDataService.parseMatchDataToPicksFormat(matchData);

      if (onDataPulled) {
        onDataPulled(parsedData);
      }

      showNotification('Match data loaded successfully!', 'success');
      setIsLoading(false);

    } catch (err) {
      setIsLoading(false);
      console.error('Error loading match data file:', err);
      setError(`Failed to load match data: ${err.message}`);
      showNotification(`Failed to load match data: ${err.message}`, 'error');
    }
  };


  return (
    <>
      <Paper
        elevation={3}
        sx={{
          padding: 2,
          marginBottom: 3,
          backgroundColor: '#1e1e1e',
          border: '1px solid #333',
          '@media (max-width: 480px)': {
            padding: 1.5,
            marginBottom: 2,
          },
          '@media (max-width: 440px)': {
            padding: 1,
            marginBottom: 1.5,
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
          <Typography
            variant="h6"
            sx={{
              color: '#fff',
              '@media (max-width: 480px)': {
              fontSize: '1rem',
              marginBottom: 1.5,
            },
            '@media (max-width: 440px)': {
              fontSize: '0.9rem',
              marginBottom: 1,
            },
          }}
        >
          Pull Match Data
        </Typography>

        {/* Show remaining pulls for free tier */}
        {userProfile && userProfile.tier === 'free' && (
          <Typography
            variant="caption"
            sx={{
              color: pullsToday >= 3 ? '#f44336' : '#4caf50',
              '@media (max-width: 480px)': {
                fontSize: '0.7rem',
              },
            }}
          >
            {pullsToday}/3 pulls today
          </Typography>
        )}
        </Box>

        <Box sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'flex-start',
          marginBottom: 2,
          '@media (max-width: 480px)': {
            gap: 1,
            marginBottom: 1.5,
            flexDirection: 'column',
          },
          '@media (max-width: 440px)': {
            gap: 0.75,
            marginBottom: 1,
          },
        }}>
          <TextField
            label="Match ID"
            variant="outlined"
            value={matchId}
            onChange={(e) => setMatchId(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && matchId.trim() && !isLoading) {
                pullMatchData();
              }
            }}
            placeholder="e.g., 8449477805"
            disabled={isLoading}
            size="small"
            sx={{
              flex: 1,
              width: '100%',
              '& .MuiInputBase-root': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                color: 'white',
                '@media (max-width: 440px)': {
                  fontSize: '0.85rem',
                },
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.2)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.3)',
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
                '@media (max-width: 440px)': {
                  fontSize: '0.85rem',
                },
              },
            }}
          />

          <Button
            variant="contained"
            color="primary"
            onClick={pullMatchData}
            disabled={isLoading || !matchId.trim()}
            startIcon={isLoading ? <CircularProgress size={16} /> : <DownloadIcon sx={{ fontSize: 18 }} />}
            sx={{
              minWidth: 120,
              '@media (max-width: 480px)': {
                width: '100%',
                minWidth: 'unset',
                fontSize: '0.875rem',
              },
              '@media (max-width: 440px)': {
                fontSize: '0.8rem',
                padding: '6px 12px',
              },
            }}
          >
            {isLoading ? 'Loading...' : 'Pull Data'}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ marginTop: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>


      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
      >
        <Alert
          severity={notification.severity}
          onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default MatchDataPull;
